import * as React from 'react'
import { HelpCircle } from 'lucide-react'
import { type ReactNode, useId } from 'react'
import { cn } from '@/lib/utils'

export type DetailCommandCardStatus = 'ready' | 'loading' | 'error'

export interface DetailCommandCardProps {
  id: string
  title: string
  windowLabel: string
  href: string
  icon: ReactNode
  kpi: ReactNode
  contextLine: ReactNode
  graphic: ReactNode
  footer: ReactNode
  status?: DetailCommandCardStatus
  errorMessage?: string
  onHelpClick?: (cardId: string) => void
  className?: string
}

const interactiveTags = new Set([
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
])

function isInteractiveRole(role: unknown) {
  return role === 'button' || role === 'link'
}

function stripInteractiveNodes(node: ReactNode): ReactNode {
  return React.Children.map(node, (child) => {
    if (!React.isValidElement(child)) return child

    const props = child.props as {
      children?: ReactNode
      role?: unknown
      tabIndex?: unknown
    }
    const isIntrinsicInteractive =
      typeof child.type === 'string' && interactiveTags.has(child.type)
    const hasInteractiveRole = isInteractiveRole(props.role)
    const hasTabStop = typeof props.tabIndex === 'number' && props.tabIndex >= 0

    if (isIntrinsicInteractive || hasInteractiveRole || hasTabStop) {
      return (
        <span className="text-xs text-muted-foreground">
          Interactive content omitted
        </span>
      )
    }

    if (props.children === undefined) return child

    return React.cloneElement(
      child as React.ReactElement<{ children?: ReactNode }>,
      undefined,
      stripInteractiveNodes(props.children),
    )
  })
}

function PlaceholderBlock({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('block rounded-md bg-muted/70', className)}
    />
  )
}

export function DetailCommandCard({
  id,
  title,
  windowLabel,
  href,
  icon,
  kpi,
  contextLine,
  graphic,
  footer,
  status = 'ready',
  errorMessage = 'Unable to load operational detail',
  onHelpClick,
  className,
}: DetailCommandCardProps) {
  const reactId = useId()
  const titleId = `operational-detail-${id}-title-${reactId}`
  const contextId = `operational-detail-${id}-context-${reactId}`
  const isLoading = status === 'loading'
  const isError = status === 'error'

  return (
    <article
      className={cn(
        'group relative flex min-h-[360px] flex-col rounded-2xl',
        'border border-border bg-card p-4 text-card-foreground shadow-sm',
        'transition-shadow hover:shadow-md focus-within:shadow-md motion-reduce:transition-none',
        className,
      )}
      aria-labelledby={titleId}
      aria-describedby={contextId}
      aria-busy={isLoading ? 'true' : undefined}
    >
      <div className="flex items-start gap-3 pr-8">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-muted/50 text-primary"
          aria-hidden="true"
        >
          {stripInteractiveNodes(icon)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start gap-2">
            <h3
              id={titleId}
              className="text-sm font-semibold uppercase tracking-wide text-foreground"
            >
              {title}
            </h3>
            <span className="rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {windowLabel}
            </span>
          </div>
          <div className="mt-3 min-h-11 text-4xl font-semibold leading-none tracking-tight">
            {isLoading ? (
              <>
                <span className="sr-only">Loading {title}</span>
                <PlaceholderBlock className="h-10 w-24" />
              </>
            ) : isError ? (
              <span className="text-destructive">—</span>
            ) : (
              stripInteractiveNodes(kpi)
            )}
          </div>
        </div>
      </div>

      <p
        id={contextId}
        className={cn(
          'mt-3 min-h-5 text-sm text-muted-foreground',
          isError && 'text-destructive',
        )}
        role={isError ? 'alert' : undefined}
      >
        {isLoading ? (
          <PlaceholderBlock className="h-4 w-2/3" />
        ) : isError ? (
          errorMessage
        ) : (
          stripInteractiveNodes(contextLine)
        )}
      </p>

      <div className="mt-4 flex min-h-[142px] items-stretch rounded-xl border border-border bg-muted/20 p-3">
        {isLoading ? (
          <div className="flex w-full flex-col justify-end gap-3" aria-hidden="true">
            <PlaceholderBlock className="h-5 w-full" />
            <PlaceholderBlock className="h-10 w-full" />
            <PlaceholderBlock className="h-16 w-full" />
          </div>
        ) : isError ? (
          <div className="flex w-full items-center justify-center text-sm text-muted-foreground">
            Detail graphic unavailable
          </div>
        ) : (
          <div className="w-full">{stripInteractiveNodes(graphic)}</div>
        )}
      </div>

      <div className="mt-auto min-h-9 pt-4 text-xs text-muted-foreground">
        {isLoading ? (
          <PlaceholderBlock className="h-4 w-1/2" />
        ) : isError ? (
          <span>Last good detail retained on the linked view.</span>
        ) : (
          stripInteractiveNodes(footer)
        )}
      </div>

      <a
        href={href}
        className={cn(
          'absolute inset-0 z-10 rounded-2xl',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        )}
        aria-labelledby={titleId}
      >
        <span className="sr-only">Open {title}</span>
      </a>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          onHelpClick?.(id)
        }}
        className={cn(
          'absolute right-3 top-3 z-20 rounded-md p-1.5',
          'bg-card/80 text-muted-foreground shadow-sm backdrop-blur',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        )}
        aria-label={`${title} help`}
      >
        <HelpCircle className="h-4 w-4" aria-hidden="true" />
      </button>
    </article>
  )
}
